/**
 * Plan price rules (pure) — mirrors the backend (subscriptions/pricing.js).
 *
 * 13d: every country has its own fixed price list, and people always SEE
 * their own money:
 *   - their country's list price when set (₦45,000 — exact);
 *   - else the GHS price at today's rate, rounded (≈ ₦45,000).
 *
 * What's CHARGED: their own currency when Eaze can take it, else USD when
 * it can, else GHS. Charged in another currency, the amount is their list
 * price converted at today's rate (whole units) — they pay what they saw;
 * with no list for their country, the GHS list. The server decides the
 * real amount; this is for the screens.
 */
import { currencyInfo, formatAmount } from "@/lib/currency";
import type { BillingCycle, LaunchPlan } from "@/constants/plans";

/** Today's rates on a GHS base: 1 GHS = rates[X] X. */
export type GhsRates = Record<string, number> | null | undefined;

export interface ResolvedPrice {
  /** What they see, in their own money (major units). */
  amount: number;
  currency: string;
  /** A fixed list price (false: converted from the GHS list, shown with ≈). */
  listed: boolean;
  /** What they're charged for one cycle. */
  charge: { amount: number; currency: string };
  /** Charged in the currency they see. */
  exact: boolean;
  /** "Charged as GH₵300 at today's rate" — null when exact. */
  chargeNote: string | null;
  /** The charge for `n` cycles (a total). */
  chargeFor: (n: number) => { amount: number; currency: string };
}

/** "Ghana cedis", "US dollars". */
export const currencyPlural = (code: string) => {
  const n = currencyInfo(code).name;
  return n && n !== code ? `${n}s` : code;
};

export function billingCurrencyFor(local: string, chargeable: string[]): string {
  if (chargeable.includes(local)) return local;
  if (chargeable.includes("USD")) return "USD";
  return "GHS";
}

/**
 * A clean figure, as the server rounds list prices (pricing.js cleanPrice):
 * two significant figures, 2½ for 1xx / 2xx. 44,850 → 45,000; 22,350 → 22,500.
 */
export function cleanPrice(v: number): number {
  if (!(v > 0) || !Number.isFinite(v)) return 0;
  const mag = 10 ** Math.floor(Math.log10(v));
  const step = Math.floor(v / mag) >= 3 ? mag / 10 : mag / 20;
  return Number((Math.round(v / step) * step).toFixed(2));
}

/** @deprecated use cleanPrice — kept for older callers. */
export const roundEstimate = cleanPrice;

/** What a price needs from a plan. */
export type PricedPlan = Pick<LaunchPlan, "monthly" | "yearly" | "prices">;

/** A plan's set price in one currency (GHS = the base list), or null. */
export function planPriceIn(plan: PricedPlan, cycle: BillingCycle, currency: string): number | null {
  if (currency === "GHS") return cycle === "monthly" ? plan.monthly : plan.yearly;
  const v = plan.prices?.[currency]?.[cycle];
  return v != null && v > 0 ? v : null;
}

/** 1 unit of `a` in `b`, through the GHS base. */
function rateBetween(a: string, b: string, rates: GhsRates): number | null {
  if (a === b) return 1;
  const of = (c: string) => (c === "GHS" ? 1 : rates?.[c] ?? null);
  const ra = of(a);
  const rb = of(b);
  return ra && rb ? rb / ra : null;
}

export function resolvePlanPrice(plan: PricedPlan, cycle: BillingCycle, local: string, chargeable: string[], rates: GhsRates): ResolvedPrice {
  const ghs = (cycle === "monthly" ? plan.monthly : plan.yearly) || 0;
  const list = planPriceIn(plan, cycle, local);
  const toLocal = rateBetween("GHS", local, rates);
  // What they see.
  const shown =
    list != null
      ? { amount: list, currency: local, listed: true }
      : toLocal && local !== "GHS"
        ? { amount: cleanPrice(ghs * toLocal), currency: local, listed: false }
        : { amount: ghs, currency: "GHS", listed: true };
  // What they're charged.
  const billing = billingCurrencyFor(local, chargeable);
  const billingRate = rateBetween(billing, local, rates); // 1 billing = x local
  let charge: { amount: number; currency: string };
  if (billing === local && list != null) charge = { amount: list, currency: local };
  else if (list != null && billingRate) charge = { amount: Math.max(1, Math.round(list / billingRate)), currency: billing };
  else {
    const set = billing === "GHS" ? ghs : planPriceIn(plan, cycle, billing);
    charge = set != null ? { amount: set, currency: billing } : { amount: ghs, currency: "GHS" };
  }
  const exact = charge.currency === shown.currency;
  return {
    ...shown,
    charge,
    exact,
    chargeNote: exact ? null : `Charged as ${formatPlanAmount(charge.amount, charge.currency)}${list != null ? " at today's rate" : ""}; your bank converts`,
    chargeFor: (n: number) => ({ amount: Math.round(charge.amount * n * 100) / 100, currency: charge.currency }),
  };
}

const formatPlanAmount = (amount: number, currency: string) => formatAmount(amount, currency, { whole: amount >= 100 && Number.isInteger(amount) });

/** The figure they see: "₦45,000", or "≈ ₦45,000" when converted from cedis. */
export function formatPlanPrice(p: ResolvedPrice): string {
  return `${p.listed ? "" : "≈ "}${formatPlanAmount(p.amount, p.currency)}`;
}

/** Any amount in a currency, the way plan prices are written. */
export const formatPrice = formatPlanAmount;
